import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { useNavigate } from "react-router-dom";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSellers, setSelectedSellers] = useState([]);
  const [editedRole, setEditedRole] = useState("");
  
  console.log(selectedSellers);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const roleOptions = ["admin", "seller", "seller_manager", "financial"];

  const fetchWithAuth = async (url, options = {}) => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      const headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        handleTokenExpiration();
        return null;
      }

      return response.ok ? await response.json() : null;
    } catch (error) {
      console.error("API Request failed", error);
      return null;
    }
  };

  const handleTokenExpiration = () => {
    localStorage.removeItem("authUser");
    navigate("/login");
  };

  useEffect(() => {
    const loadData = async () => {
      const usersData = await fetchWithAuth(`${apiUrl}/api/users`);
      const sellersData = await fetchWithAuth(`${apiUrl}/api/sellers`);

      setUsers(usersData || []);
      setSellers(sellersData || []);
    };

    loadData();
  }, [apiUrl]);

  const handleOpenAssignModal = async (user) => {
    const assignedSellers = await fetchWithAuth(`${apiUrl}/api/manager-sellers/${user.id}`);
    setSelectedSellers(assignedSellers ? assignedSellers.map(seller => seller.id) : []);
    setSelectedUser(user);
    setAssignModalOpen(true);
  };

  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setEditedRole(user.role);
    setEditModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    const success = await fetchWithAuth(`${apiUrl}/api/users/${selectedUser.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: editedRole }),
    });

    if (success) {
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === selectedUser.id ? { ...user, role: editedRole } : user
      ));
      setEditModalOpen(false);
    }
  };

  const handleAssignSellers = async () => {
    const success = await fetchWithAuth(`${apiUrl}/api/manager-sellers/${selectedUser.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sellerIds: selectedSellers }),
    });

    if (success) {
      setAssignModalOpen(false);
    }
  };

  const toggleSellerSelection = (sellerId) => {
   console.log('toggle',sellerId);
    setSelectedSellers((prev) =>
      prev.includes(sellerId)
        ? prev.filter((id) => id !== sellerId)
        : [...prev, sellerId]
    );
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    const success = await fetchWithAuth(`${apiUrl}/api/users/${selectedUser.id}`, {
      method: "DELETE",
    });

    if (success) {
      setUsers((prev) => prev.filter((user) => user.id !== selectedUser.id));
      setSelectedUser(null);
      setDeleteModalOpen(false);
    }
  };

  const tableStyles = {
    table: {
      tableLayout: "fixed",
      width: "100%",
    },
    column: {
      id: { width: "10%" },
      email: { width: "25%" },
      username: { width: "20%" },
      role: { width: "15%" },
      actions: { width: "30%" },
    },
  };

  return (
    <Container style={{ paddingTop: "80px" }}>
      <h1 className="mb-4">User Management</h1>
      <Card>
        <CardBody className="p-0">
          <Table bordered hover responsive style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.column.id}>ID</th>
                <th style={tableStyles.column.email}>Email</th>
                <th style={tableStyles.column.username}>Username</th>
                <th style={tableStyles.column.role}>Role</th>
                <th style={tableStyles.column.actions}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td style={tableStyles.column.id}>{user.id}</td>
                  <td style={tableStyles.column.email}>{user.email}</td>
                  <td style={tableStyles.column.username}>{user.username}</td>
                  <td style={tableStyles.column.role}>{user.role}</td>
                  <td style={tableStyles.column.actions}>
                    <div className="d-flex gap-2">
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => handleOpenEditModal(user)}
                      >
                        Edit
                      </Button>
                      {user.role === "seller_manager" && (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleOpenAssignModal(user)}
                        >
                          Assign Sellers
                        </Button>
                      )}
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteModalOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Delete Modal */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          Delete User
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete user {selectedUser?.username}?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteUser}>Delete</Button>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)}>
        <ModalHeader toggle={() => setEditModalOpen(false)}>
          Edit User {selectedUser?.username}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="roleSelect">Role</Label>
            <Input
              type="select"
              id="roleSelect"
              value={editedRole}
              onChange={(e) => setEditedRole(e.target.value)}
            >
              {roleOptions.map(role => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleUpdateUser}>Save Changes</Button>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Assign Sellers Modal */}
      <Modal isOpen={assignModalOpen} toggle={() => setAssignModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setAssignModalOpen(false)}>
          Assign Sellers to {selectedUser?.username}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <strong>Selected Sellers:</strong>
            {sellers
              .filter((seller) => selectedSellers.includes(seller.id))
              .map((seller) => seller.username)
              .join(", ") || "None"}
          </div>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {sellers.map((seller) => (
              <FormGroup check key={seller.id} className="mb-2">
                <Input
                  type="checkbox"
                  id={`seller-${seller.id}`}
                  checked={selectedSellers.includes(seller.id)}
                  onChange={() => toggleSellerSelection(seller.id)}
                  onClick={() => toggleSellerSelection(seller.id)}
                  className="me-2"
                />
                <Label check for={`seller-${seller.id}`}>
                  {seller.username} ({seller.email})
                </Label>
              </FormGroup>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAssignSellers}>Save Assignments</Button>
          <Button color="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default UsersList;